/**
 * SidebarStep6_FutureClosure - Data & Documents Review (Future Closure Path)
 *
 * Per the 2026-07-08 architecture decision, the wizard is the data specialist's
 * SETUP + VERIFICATION surface — NOT an analysis surface. This step replaces the old
 * float-engine "Pro Forma Review + Monte Carlo" (all of that analysis now lives on the
 * ACCOUNTANT dashboard) with a review of the case's DOCUMENT status.
 *
 * It reflects the AI data specialist's work: the same unified FC document set
 * (REQUIREMENTS_MATRIX['FC'] — closure order, reopening notice, remediation contract,
 * plus financials) that the AI chases and auto-requests from the client. The human
 * specialist reviews what's received vs outstanding and hands off; the AI can also
 * advance the case on its own when no human is needed.
 *
 * Data source: GET /api/v1/reports/{id}/document-requirements (check_document_completeness).
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/TestAuthContext';
import axios from '@/lib/http';
import WizardV2Layout from './WizardV2Layout';
import type { QAItem } from './WizardSidebar';
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  Clock,
  Loader2,
  Bot,
  MailCheck,
  ArrowRight,
  Upload,
} from 'lucide-react';

interface SidebarStep6FutureClosureProps {
  onBack: () => void;
  onComplete: () => void;
  goToStep?: (step: number) => void;
  data: any;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  highestCompletedStep?: number;
  backendCompletionPercent?: number;
  claimId?: string;
  reviewCount?: number;
  anomalyCount?: number;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface DocRequirement {
  doc_type: string;
  display_name: string;
  status: string;          // required | optional | conditional | n/a
  uploaded: boolean;
  upload_status: string;   // parsed | uploaded | missing | not_required
  file_count: number;
}

interface DocResult {
  case_type: string;
  case_type_name: string;
  requirements: DocRequirement[];
  completion: { required_count: number; uploaded_count: number; percentage: number; ready_for_accountant: boolean };
  blockers: Array<{ doc_type: string; display_name: string; message: string }>;
  warnings: Array<{ doc_type: string; display_name: string; message: string }>;
}

export default function SidebarStep6_FutureClosure({
  onBack,
  onComplete,
  goToStep,
  data,
  onSave,
  isSaving,
  lastSaved,
  highestCompletedStep,
  backendCompletionPercent,
  claimId,
}: SidebarStep6FutureClosureProps) {
  const { getToken } = useAuth();
  const reportId = claimId || data.report_id || data.generated_report_id;

  const [result, setResult] = useState<DocResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!reportId) return;
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const resp = await axios.get(
          `${BACKEND_URL}/api/v1/reports/${reportId}/document-requirements`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!cancelled) setResult(resp.data);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.detail || 'Could not load document status.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [reportId, getToken]);

  const requirements = result?.requirements || [];
  const received = requirements.filter((r) => r.uploaded);
  const outstanding = requirements.filter((r) => !r.uploaded && r.upload_status !== 'not_required');
  const blockers = result?.blockers || [];
  const pct = result?.completion?.percentage ?? 0;

  const buildSections = () => [
    { id: 'status', label: 'Document Status', isActive: true, isComplete: blockers.length === 0, onClick: () => document.getElementById('status-section')?.scrollIntoView({ behavior: 'smooth' }) },
    { id: 'outstanding', label: 'Outstanding', isActive: false, isComplete: outstanding.length === 0, onClick: () => document.getElementById('outstanding-section')?.scrollIntoView({ behavior: 'smooth' }) },
  ];

  const buildQAItems = (): QAItem[] => {
    const items: QAItem[] = [];
    if (result) {
      items.push({ id: 'received', label: `${received.length} received`, status: 'complete' });
      if (blockers.length > 0) items.push({ id: 'blockers', label: `${blockers.length} required missing`, status: 'warning' });
      else if (outstanding.length > 0) items.push({ id: 'outstanding', label: `${outstanding.length} recommended outstanding`, status: 'warning' });
    }
    return items;
  };

  const StatusBadge = ({ r }: { r: DocRequirement }) => {
    if (r.uploaded) {
      return (
        <span className="cs-meta inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'transparent', color: 'var(--color-verified)' }}>
          <CheckCircle className="w-3.5 h-3.5" /> {r.upload_status === 'parsed' ? 'Parsed' : 'Uploaded'}
        </span>
      );
    }
    const isRequired = r.status === 'required';
    return (
      <span className="cs-meta inline-flex items-center gap-1 px-2 py-1 rounded-md"
        style={isRequired
          ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
          : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
        {isRequired ? <AlertTriangle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
        {isRequired ? 'Required — requested' : 'Recommended'}
      </span>
    );
  };

  const DocRow = ({ r }: { r: DocRequirement }) => (
    <div className="flex items-center gap-3 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
      <FileText className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
      <div className="flex-1 min-w-0">
        <p className="cs-body truncate" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{r.display_name}</p>
        {r.file_count > 0 && <p className="cs-meta">{r.file_count} file{r.file_count > 1 ? 's' : ''}</p>}
      </div>
      <StatusBadge r={r} />
    </div>
  );

  return (
    <WizardV2Layout
      currentStep={6}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Data & Documents Review"
      stepSubtitle="Review the documents your AI data specialist has gathered for this case"
      completionPercent={backendCompletionPercent ?? pct}
      grade={blockers.length === 0 ? 'A' : 'B'}
      calculationMode="future_closure"
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      showNext={false}
      claimId={claimId}
    >
      <div className="p-6 lg:p-8 space-y-6">

        {/* AI data specialist banner */}
        <div className="rounded-xl p-6" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--color-bg-surface)' }}>
              <Bot className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
            </div>
            <div>
              <h2 className="cs-h2" style={{ color: 'var(--color-needs-human-text)' }}>Your AI data specialist is on this case</h2>
              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
                It identifies the documents a forced-closure claim needs, automatically requests any that are
                missing from the client, and can advance the case on its own. Review what's in below — analysis
                (pro-forma, sensitivity, exhibits) runs later on the accountant dashboard.
              </p>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 cs-meta p-4" style={{ color: 'var(--color-text-muted)' }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading document status…
          </div>
        )}
        {!loading && error && (
          <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-warning, #b45309)' }} />
            <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>{error}</p>
          </div>
        )}

        {!loading && result && (
          <>
            {/* Completion summary */}
            <div id="status-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                    <FileText className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  </div>
                  <div>
                    <h3 className="cs-h3">{result.case_type_name || 'Forced Closure'} — Required Documents</h3>
                    <p className="cs-meta">{result.completion.uploaded_count} of {result.completion.required_count} required received</p>
                  </div>
                </div>
                <span className="cs-figure" style={{ fontSize: '1.5rem', color: blockers.length === 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>{pct}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
                <div className="h-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, background: blockers.length === 0 ? 'var(--color-verified)' : 'var(--color-brand)' }} />
              </div>

              {received.length > 0 && (
                <div className="mt-5 space-y-2">
                  <p className="cs-label" style={{ color: 'var(--color-text-muted)' }}>Received</p>
                  {received.map((r) => <DocRow key={r.doc_type} r={r} />)}
                </div>
              )}
            </div>

            {/* Outstanding */}
            <div id="outstanding-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                  <MailCheck className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                </div>
                <div>
                  <h3 className="cs-h3">Outstanding Documents</h3>
                  <p className="cs-meta">Automatically requested from the client by the AI data specialist</p>
                </div>
              </div>
              {outstanding.length === 0 ? (
                <div className="flex items-center gap-2 p-4 rounded-lg" style={{ background: 'transparent' }}>
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                  <span className="cs-body" style={{ color: 'var(--color-verified)' }}>All documents are in — nothing outstanding.</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {outstanding.map((r) => <DocRow key={r.doc_type} r={r} />)}
                </div>
              )}

              {/* Manual upload escape hatch */}
              <button
                onClick={() => goToStep?.(3)}
                className="mt-4 flex items-center gap-2 cs-meta font-medium"
                style={{ color: 'var(--color-needs-human-text)' }}
              >
                <Upload className="w-4 h-4" /> Upload documents manually (Step 3)
              </button>
            </div>
          </>
        )}

        {/* Continue to hand-off */}
        <div className="pt-2">
          <button
            onClick={() => goToStep?.(7)}
            className="cs-body w-full font-bold py-4 px-8 rounded-xl shadow-lg transition-all flex items-center justify-center gap-3"
            style={{ fontSize: '1.125rem', background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            Continue to Hand-off <ArrowRight className="w-5 h-5" />
          </button>
          {blockers.length > 0 && (
            <p className="cs-meta text-center mt-3" style={{ color: 'var(--color-text-muted)' }}>
              {blockers.length} required document{blockers.length > 1 ? 's are' : ' is'} still outstanding — the AI specialist is requesting {blockers.length > 1 ? 'them' : 'it'} from the client. You can still proceed; the accountant will confirm before analysis.
            </p>
          )}
        </div>

      </div>
    </WizardV2Layout>
  );
}
